import React, { RefObject } from 'react';
import clsx from 'clsx';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import TabBarActions, { TabBarAction } from '../../../../common/TabBarActions/TabBarActions';
import { dataNotAvailable } from '../../../../assets/hostedassets';
import styles from './GstOverview.module.scss';

export interface GstSubTabEntry {
    id: string;
    label: string;
    disabled?: boolean;
    tooltip?: string;
}

export type GstSubTabNoDataMap = Record<string, { tooltip?: string }>;

interface GstSubTabBarProps {
    subTabs: GstSubTabEntry[];
    activePill: string;
    onSelect: (pillId: string) => void;
    noDataPills?: GstSubTabNoDataMap;
    /** Bar actions; with them the pills share one fitted row and fold into ⋯ when narrow. Omitted on PDF and mobile. */
    actions?: TabBarAction[];
    menuTriggerRef?: RefObject<HTMLButtonElement>;
}

const DEFAULT_NO_DATA_TOOLTIP = 'Data not available, please connect or upload data to access this module';

function GstSubTabBar({ subTabs, activePill, onSelect, noDataPills, actions, menuTriggerRef }: GstSubTabBarProps) {
    // The bar still renders with no pills when it has actions: subTabs come from the Polus dashboard
    // config, which arrives async and is empty on first paint, so returning null here would make the
    // actions flicker out — or stay missing for a PNO whose GST tab has no sub-tabs configured.
    if (!subTabs?.length && !actions) return null;
    const pills = subTabs.map((pill) => {
        const noDataEntry = noDataPills?.[pill.id];
        const isNoData = !!noDataEntry;
        const isDisabled = !!pill.disabled || isNoData;
        const isActive = activePill === pill.id;
        const pillNode = (
            <button
                key={pill.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-disabled={isDisabled}
                className={clsx(styles.Pill, {
                    [styles.PillActive]: isActive,
                    [styles.PillDisabled]: isDisabled,
                })}
                onClick={isDisabled ? undefined : () => onSelect(pill.id)}
            >
                <span>{pill.label}</span>
                {isNoData && <img src={dataNotAvailable} alt="" className={styles.PillNoDataIcon} aria-hidden="true" />}
            </button>
        );
        const tooltipText = isNoData ? noDataEntry?.tooltip || DEFAULT_NO_DATA_TOOLTIP : pill.tooltip || 'Coming soon';
        const inline = isDisabled ? (
            <Tooltip key={pill.id} content={tooltipText} direction="bottom" fitContent usePortal>
                {pillNode}
            </Tooltip>
        ) : (
            pillNode
        );
        return {
            id: pill.id,
            label: pill.label,
            active: isActive,
            inline,
            menuItem: { name: pill.label, onClick: () => onSelect(pill.id), disabled: isDisabled },
        };
    });
    return (
        <div className={styles.SubTabBar}>
            {actions ? (
                <TabBarActions pills={pills} pillsRole="tablist" actions={actions} menuTriggerRef={menuTriggerRef} />
            ) : (
                <div className={styles.PillBar} role="tablist">
                    {pills.map((pill) => pill.inline)}
                </div>
            )}
        </div>
    );
}

export default GstSubTabBar;
